import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback } from 'react';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

const MAX_LOCATIONS = 50;

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

interface IUseLocationsReturn {
  filteredTargetings: Targeting[];
  remainingLocations: number;
  canAddMore: boolean;
  handleAddFilter: () => void;
  handleRemoveFilter: (targeting: Targeting) => void;
  handleSaveCustomSet: () => void;
}

export function useLocationFilters({
  campaign,
  targetings,
}: Readonly<IProps>): IUseLocationsReturn {
  const { Targetings } = useStore();

  // Direct computation - MobX will handle reactivity
  const filteredTargetings = targetings.filter(
    (t) => !t.get('is_archived') && t.get('kind') === 'geo',
  );

  const remainingLocations = MAX_LOCATIONS - filteredTargetings.length;
  const canAddMore = remainingLocations > 0;

  const handleRemoveFilter = useCallback(
    (targeting: Targeting): void => {
      if (!targeting.isEphemeral) {
        targeting.set({ is_archived: true });
      } else {
        runInAction(() => {
          targetings.remove(targeting);
          Targetings.remove(targeting.id);
        });
      }
    },
    [targetings, Targetings],
  );

  const handleAddFilter = useCallback((): void => {
    const currentFiltered = targetings.filter(
      (t) => !t.get('is_archived') && t.get('kind') === 'geo',
    );

    if (currentFiltered.length >= MAX_LOCATIONS) {
      return;
    }

    const model = Targetings.create({
      parent: campaign.get('id'),
      kind: 'geo',
      included: true,
    });
    runInAction(() => {
      targetings.push(model);
    });
  }, [campaign, targetings, Targetings]);

  const handleSaveCustomSet = useCallback((): void => {
    // TODO: Implement save custom set
  }, []);

  return {
    filteredTargetings,
    remainingLocations,
    canAddMore,
    handleAddFilter,
    handleRemoveFilter,
    handleSaveCustomSet,
  };
}

export default useLocationFilters;
